import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  DatabaseZap,
  MonitorSmartphone,
  Inbox as InboxIcon,
  CheckSquare,
  Workflow as WorkflowIcon,
  TrendingUp,
  Video,
  FileText,
  Mail,
} from "lucide-react";
import { ContactAvatar } from "@/components/ui/contact-avatar";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { WorldMapField } from "@/features/auth/components/WorldMapField";
import { trackEvent } from "@/shared/lib/posthog";
import { DOWNLOAD_PAGE_URL, buildDownloadUrl, browserPlatform } from "@/shared/lib/desktop-download";
import { PENDING_MOBILE_HANDOFF_KEY, inNativeShell, isMobileHandoffLeg } from "@/shared/lib/mobile-handoff";
// Config only — this is a plain typed fetch in the entry chunk. It must NEVER
// grow into an import of the phone-login page or of features/auth/lib/phone,
// which would drag libphonenumber's metadata into the bundle every device
// downloads before it can render anything. See e2e/smoke.spec.ts.
import { otpApi } from "@/api/endpoints/auth";
import { Spinner } from "@/components/ui/spinner";

const PENDING_HANDOFF_KEY = "ubik_desktop_handoff_pending";

// Matches the marketing site's type stack (Montserrat display / Noto Sans
// body / IBM Plex Mono labels) rather than the app-shell's Inter/JetBrains
// tokens, since this page's brief is to look like the landing page.
const FONT_DISPLAY = '"Montserrat", sans-serif';
const FONT_BODY = '"Noto Sans", "Inter", sans-serif';
const FONT_MONO = '"IBM Plex Mono", "JetBrains Mono", monospace';

const STATS = [
  { value: "$700M+", label: "Perishables traded" },
  { value: "20x", label: "Faster operations" },
  { value: "$200K", label: "Avg. annual savings" },
];

// "ubik Meetings" is the one clickable trust badge — it opens the desktop
// download page, OS-themed via the same buildDownloadUrl/browserPlatform
// logic the app's desktop-nudge hook uses (see import above), so this never
// drifts from the one place that already got OS detection right.
const TRUST = [
  { icon: ShieldCheck, label: "SOC 2 Type II & GDPR", sub: "Audited & certified" },
  { icon: DatabaseZap, label: "Data never trained on", sub: "Your data stays yours" },
  { icon: MonitorSmartphone, label: "ubik Meetings", sub: "Desktop & Mac", downloadable: true },
];

// ─── Right-panel product preview ──────────────────────────────────────────
// Cycles through the app's real surfaces (Inbox, Tasks, Workflows, Market
// Intel, Meetings, Artifacts) instead of a generic chat mock — a compact,
// theme-aware card, not a full-bleed dark stage. Deliberately small, with
// room left below for a landing-page-style illustration later.
// metaTone follows the DS rule (see CLAUDE.md / SKILL.md "ink, not grey"):
// muted-foreground is reserved for genuine timestamps/disabled state; actual
// KPI values and status get foreground, and a notable positive number (a
// price moving up) gets the brand blue as an accent — not grey by default.
const SURFACES = [
  {
    key: "inbox",
    label: "Inbox",
    icon: InboxIcon,
    // No leading tag on these two: the sender's contact square is the row's
    // left-most element, the way it is in the real Inbox.
    rows: [
      { contact: "Priya Menon", text: "Buyer asks for availability — Shrimp L2, 240 CT", meta: "New" },
      { contact: "Dev Raman", text: "PO-2381 needs SKU mapping before release", meta: "2h", metaTone: "muted" },
    ],
  },
  {
    key: "email",
    label: "Email",
    icon: Mail,
    rows: [
      { tag: "Drafted", text: "Renewal follow-up to MFL — ready to send", meta: "Draft" },
      { tag: "Sent", text: "Quote update sent to 3 vendors", meta: "2m", metaTone: "muted" },
    ],
  },
  {
    key: "tasks",
    label: "Tasks",
    icon: CheckSquare,
    // `pri` drives the blue-square-dot glyph in the left-most slot, matching
    // the PRI column in the Tasks surface: filled blue = live, grey = parked.
    rows: [
      { pri: "active", tag: "Open", text: "Confirm COA for BL-2408-219", meta: "Today", metaTone: "muted" },
      { pri: "muted", tag: "Open", text: "Send revised quote to MFL", meta: "Tomorrow", metaTone: "muted" },
    ],
  },
  {
    key: "workflows",
    label: "Workflows",
    icon: WorkflowIcon,
    rows: [
      { tag: "Running", text: "Import clearance — 6 of 9 steps", meta: "62%", metaTone: "primary" },
      { tag: "Queued", text: "Demurrage review", meta: "Next" },
    ],
  },
  {
    key: "markets",
    label: "Market Intel",
    icon: TrendingUp,
    rows: [
      { tag: "Up", text: "Shrimp L2 spot", meta: "+4.2%", metaTone: "primary" },
      { tag: "Flat", text: "Salmon fillet — 30d avg", meta: "$6.80/kg" },
    ],
  },
  {
    key: "meetings",
    label: "Meetings",
    icon: Video,
    rows: [
      { tag: "Fri", text: "Buyer sync — renewal terms", meta: "10:00", metaTone: "muted" },
      { tag: "Ready", text: "Transcript — MFL renewal call", meta: "Today", metaTone: "muted" },
      { tag: "Pre-read", text: "LinkedIn profile + last-call memory summarized", meta: "Ready" },
    ],
  },
  {
    key: "artifacts",
    label: "Artifacts",
    icon: FileText,
    rows: [
      { tag: "PDF", text: "Q3 shipment report", meta: "Ready" },
      { tag: "DOC", text: "Customs packet — BL-2408-219", meta: "Ready" },
    ],
  },
] as const;

const META_TONE_CLASS: Record<string, string> = {
  muted: "text-muted-foreground",
  primary: "text-primary",
};

// The PRI glyph from the Tasks surface: a 2x2 block of squares, blue while
// the task is live and grey once it's parked. Same square vocabulary as the
// `ubik` wordmark dot, so the preview reads as one system rather than
// borrowing a second icon set.
function PriorityDots({ tone }: { tone: "active" | "muted" }) {
  return (
    <span
      className="grid shrink-0 grid-cols-2 gap-[1.5px]"
      aria-hidden="true"
    >
      {Array.from({ length: 4 }, (_, n) => (
        <span
          key={n}
          className={`h-[4px] w-[4px] ${tone === "active" ? "bg-primary" : "bg-muted-foreground/45"}`}
        />
      ))}
    </span>
  );
}

function ProductPreview() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const id = setInterval(() => setI((n) => (n + 1) % SURFACES.length), 3400);
    return () => clearInterval(id);
  }, []);

  const surface = SURFACES[i];
  const Icon = surface.icon;

  return (
    <div className="w-full max-w-md border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Icon className="h-3.5 w-3.5 text-primary" />
        <span
          className="ubik-label text-muted-foreground"
          style={{ fontFamily: FONT_MONO }}
        >
          {surface.label}
        </span>
      </div>

      <div className="flex flex-col">
        {surface.rows.map((row) => (
          <div
            key={row.text}
            className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 last:border-b-0"
          >
            <div className="flex min-w-0 items-center gap-3">
              {"pri" in row && <PriorityDots tone={row.pri} />}
              {"contact" in row && (
                // Ink fill, matching the near-black contact tiles in the
                // Tasks prototype. `--foreground`/`--background` rather than a
                // literal black so it inverts correctly if this card is ever
                // rendered on a dark surface.
                <ContactAvatar
                  name={row.contact}
                  size="xs"
                  className="border-transparent bg-foreground text-background"
                />
              )}
              {"tag" in row && (
                <span
                  className="shrink-0 ubik-label text-primary"
                  style={{ fontFamily: FONT_MONO }}
                >
                  {row.tag}
                </span>
              )}
              <span className="truncate text-xs text-foreground">{row.text}</span>
            </div>
            <span
              className={`shrink-0 text-xs ${META_TONE_CLASS["metaTone" in row ? row.metaTone ?? "" : ""] ?? "text-foreground"}`}
              style={{ fontFamily: FONT_MONO }}
            >
              {row.meta}
            </span>
          </div>
        ))}
      </div>

      <div className="flex justify-center gap-1.5 border-t border-border py-2.5">
        {SURFACES.map((s, n) => (
          <button
            key={s.key}
            onClick={() => setI(n)}
            aria-label={`Show ${s.label}`}
            className={`h-1 transition-all ${n === i ? "w-4 bg-primary" : "w-1.5 bg-border"}`}
          />
        ))}
      </div>
    </div>
  );
}

// Below sm: a horizontal snap-scroll carousel, auto-advancing every 3.4s just
// like ProductPreview above it (with matching dot indicators) — swipe still
// works too, it just also cycles on its own instead of sitting static. sm and
// up restores the static 3-column grid (no scrolling, so no auto-advance).
function TrustCarousel({ downloadUrl }: { downloadUrl: string }) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [i, setI] = useState(0);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const id = setInterval(() => setI((n) => (n + 1) % TRUST.length), 3400);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.children[i] as HTMLElement | undefined;
    card?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }, [i]);

  return (
    <div>
      <div
        ref={scrollerRef}
        className="flex snap-x snap-mandatory overflow-x-auto border-t border-border bg-border [-ms-overflow-style:none] scrollbar-none sm:grid sm:grid-cols-3 sm:gap-px sm:overflow-visible [&::-webkit-scrollbar]:hidden"
      >
        {TRUST.map(({ icon: Icon, label, sub, downloadable }) =>
          downloadable ? (
            // The hover fill is a solid blue (per the DS override: selected/
            // active = blue fill, not a tint) — so icon + text must flip to
            // primary-foreground (white) on hover too, or they read as
            // near-invisible dark-on-blue / blue-on-blue.
            <a
              key={label}
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-full shrink-0 snap-center items-center gap-2.5 bg-card px-5 py-3.5 transition-colors hover:bg-primary sm:w-auto"
            >
              <Icon className="h-4 w-4 shrink-0 text-primary transition-colors group-hover:text-primary-foreground" />
              <div>
                <div className="text-xs font-medium leading-tight transition-colors group-hover:text-primary-foreground">
                  {label}
                </div>
                <div className="text-xs leading-tight text-muted-foreground transition-colors group-hover:text-primary-foreground/85">
                  {sub}
                </div>
              </div>
            </a>
          ) : (
            <div key={label} className="flex w-full shrink-0 snap-center items-center gap-2.5 bg-card px-5 py-3.5 sm:w-auto">
              <Icon className="h-4 w-4 shrink-0 text-primary" />
              <div>
                <div className="text-xs font-medium leading-tight">{label}</div>
                <div className="text-xs leading-tight text-muted-foreground">{sub}</div>
              </div>
            </div>
          )
        )}
      </div>
      <div className="flex justify-center gap-1.5 border-t border-border py-2.5 sm:hidden">
        {TRUST.map((t, n) => (
          <button
            key={t.label}
            onClick={() => setI(n)}
            aria-label={`Show ${t.label}`}
            className={`h-1 transition-all ${n === i ? "w-4 bg-primary" : "w-1.5 bg-border"}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function LoginRedesign() {
  const { startGoogleAuth, startMicrosoftAuth, error, loading } = useAuth();
  // "The app" in both of its guises: the in-app login screen the shell renders
  // itself (WebView, plain /login — no query string), and the external-browser
  // leg it opens (?from=mobile, set by native-bridge openNativeLogin). The first
  // was missed before, so the shell's own first screen — the one a reviewer
  // sees before anything else — carried the web's self-serve wording.
  const fromMobileApp = useMemo(
    () => typeof window !== "undefined" && (isMobileHandoffLeg() || inNativeShell()),
    [],
  );
  // ?provider=google|microsoft, only ever set together with ?from=mobile: the
  // person already chose a provider on the in-app screen, so this page starts
  // that OAuth immediately instead of asking again (see the effect below).
  const mobileProvider = useMemo<"google" | "microsoft" | null>(() => {
    if (typeof window === "undefined" || !fromMobileApp) return null;
    const p = new URLSearchParams(window.location.search).get("provider");
    return p === "google" || p === "microsoft" ? p : null;
  }, [fromMobileApp]);
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!mobileProvider || autoStartedRef.current) return;
    autoStartedRef.current = true;
    if (mobileProvider === "google") startGoogleAuth();
    else void startMicrosoftAuth();
  }, [mobileProvider, startGoogleAuth, startMicrosoftAuth]);

  // Is phone + OTP sign-in switched on for this deployment? FAIL CLOSED: the
  // button only appears once the backend has said yes, so an unreachable
  // /otp/config leaves the two OAuth buttons exactly as they were rather than
  // offering a route that dead-ends. Deliberately a server call, not a
  // build-time VITE_ flag — the mobile bundle has no OTA channel, so a
  // compiled-in flag would be frozen until the next store release.
  const [phoneAuthEnabled, setPhoneAuthEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    otpApi
      .config()
      .then((cfg) => {
        if (!cancelled) setPhoneAuthEnabled(cfg?.phone_auth_enabled === true);
      })
      .catch(() => {
        if (!cancelled) setPhoneAuthEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Styled identically to the two provider buttons below. Nothing is hidden by
  // its presence — OAuth stays fully available in every case; this is a THIRD
  // door for people who have no email address to put through either of them.
  const phoneButton = phoneAuthEnabled ? (
    <Link
      to="/login/phone"
      onClick={() => trackEvent("login_provider_clicked", { provider: "phone_otp" })}
      className="flex w-full items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-row font-medium text-foreground transition-colors hover:bg-accent"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="6" y="2" width="12" height="20" rx="2" />
        <path d="M11 18h2" />
      </svg>
      Continue with a mobile number
    </Link>
  ) : null;

  // This page is always light, at every width. Its palette (blue stat band,
  // hairline card borders, the marketing type stack) is tuned for light only,
  // and a signed-out visitor has no reason to be choosing a theme yet — the
  // real toggle lives on the Settings page. The stored preference is left
  // alone in localStorage and reapplied on unmount, so a dark-mode user drops
  // back into dark the moment they're signed in rather than being reset.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark");
    return () => {
      try {
        if (localStorage.getItem("ubik-theme") === "dark") root.classList.add("dark");
      } catch {
        // Blocked-cookie context — light is the correct fallback anyway.
      }
    };
  }, []);

  // Same OS-themed URL the desktop-nudge hook builds — see the import above.
  const downloadUrl = useMemo(() => buildDownloadUrl(DOWNLOAD_PAGE_URL, browserPlatform()), []);

  // When the desktop app opens /login?from=desktop, stash the handoff flag so
  // AuthenticatedApp redirects to /auth/desktop after OAuth completes.
  // The mobile shells open /login?from=mobile the same way. Without this flag
  // the OAuth callback dropped a FIRST-TIME user on the web workbench (only
  // /auth/mobile used to set it), and the app got its token only on the
  // second attempt, when a web session already existed.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") === "desktop") {
      sessionStorage.setItem(PENDING_HANDOFF_KEY, "1");
    }
    if (params.get("from") === "mobile") {
      sessionStorage.setItem(PENDING_MOBILE_HANDOFF_KEY, "1");
    }
  }, []);

  // Provider handoff from the app: the shell already showed the sign-in
  // choice, so this leg only bridges to the provider. A start error drops
  // through to the full page below, where it is shown with the buttons.
  if (mobileProvider && !error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-5 bg-background px-8 text-center text-foreground" style={{ fontFamily: FONT_BODY }}>
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold tracking-[-0.02em]" style={{ fontFamily: FONT_DISPLAY }}>
            ubik
          </span>
        </div>
        <Spinner className="size-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {mobileProvider === "google" ? "Opening Google sign-in…" : "Opening Microsoft sign-in…"}
        </p>
      </div>
    );
  }

  // The app shell locks document scrolling globally (html/body in index.css
  // are height:100% + overflow:hidden) because the workbench scrolls its own
  // inner panels. This page is a long single-document layout, so it has to own
  // its scrolling or anything past the fold is clipped and unreachable — which
  // is what happened on phones: browser chrome shrinks the visible area below
  // this page's content height, and with no scroll container the top row
  // (wordmark + graphic button) or the trust bands fell outside it with no way
  // to reach them. Height is dvh, not vh: on iOS vh is the chrome-collapsed
  // height, so a vh-sized box is taller than what's actually visible.
  // overflow-x stays hidden to keep clipping the snap-scroll trust row.
  return (
    <div className="relative h-dvh overflow-y-auto overflow-x-hidden bg-background text-foreground" style={{ fontFamily: FONT_BODY }}>
      {/* Dot-matrix world map with dithered ship traffic, spread across the
          full width of the page — behind both the sign-in column and the
          preview panel — and filling the top/bottom whitespace the layout
          would otherwise leave bare.
          Desktop only (lg:): below that the page is a stacked single column
          with no spare margin to fill, and a background layer would only
          crowd it.
          The mask holds the middle band fully transparent, which is what
          keeps the sign-in form, the stat row and the preview card on clean
          paper — the map only ever reads above and below them. `fixed` rather
          than `absolute` so it stays viewport-anchored inside this scroll
          container. */}
      <div
        className="pointer-events-none fixed inset-0 hidden lg:block"
        // Clear window is 29%-70%. Measured against the real layout at 900px
        // tall: stat band starts at 276 (30.7%), trust row ends at 625
        // (69.4%), and the sign-in column's headline/buttons/privacy link all
        // sit inside that. So the map is fully absent behind every piece of
        // reading matter and only ramps in across the empty margins.
        style={{
          maskImage:
            "linear-gradient(to bottom, black 0%, black 20%, transparent 29%, transparent 70%, black 79%, black 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 0%, black 20%, transparent 29%, transparent 70%, black 79%, black 100%)",
        }}
      >
        <WorldMapField className="h-full w-full" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-dvh max-w-[1400px] flex-col lg:flex-row">
        {/* ── Left: sign-in panel ──────────────────────────────────────── */}
        <div className="flex w-full flex-col justify-center px-8 py-10 sm:px-14 lg:w-[440px] lg:flex-none lg:px-12">
          <div className="mb-10 flex items-center gap-2">
            <span className="text-lg font-semibold tracking-[-0.02em]" style={{ fontFamily: FONT_DISPLAY }}>
              ubik
            </span>
          </div>

          {/* The native apps open this page in an in-app browser (?from=mobile).
              Store review reads the sign-in screen literally: the apps are for
              existing organisation accounts (App Store 4.8 business exception,
              no in-app account creation), so the self-serve wording stays web-only. */}
          <h1 className="text-[1.9rem] font-semibold leading-[1.15] tracking-[-0.02em]" style={{ fontFamily: FONT_DISPLAY }}>
            {fromMobileApp ? "Sign in to your workspace" : "Make ubik yours"}
          </h1>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
            {fromMobileApp
              ? "Use your organisation's Google or Microsoft account. Don't have access yet? Ask your Ubik administrator."
              : "Sign in to continue"}
          </p>

          <div className="mt-8 flex flex-col gap-2.5">
            {/* First in the stack inside the native shells (?from=mobile):
                that build's audience is the floor, where "no email address" is
                the common case rather than the exception. On the web it goes
                last, under the two provider buttons. */}
            {fromMobileApp ? phoneButton : null}
            <button
              onClick={() => {
                trackEvent("login_provider_clicked", { provider: "google" });
                startGoogleAuth();
              }}
              disabled={loading}
              className="flex w-full items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-row font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <Spinner className="size-[18px] shrink-0" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" className="shrink-0">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              )}
              {loading ? "Signing in…" : "Continue with Google"}
            </button>

            <button
              onClick={() => {
                trackEvent("login_provider_clicked", { provider: "microsoft" });
                startMicrosoftAuth();
              }}
              disabled={loading}
              className="flex w-full items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-row font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <div className="h-[18px] w-[18px] shrink-0" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 23 23" className="shrink-0">
                  <path fill="#f35325" d="M1 1h10v10H1z" />
                  <path fill="#81bc06" d="M12 1h10v10H12z" />
                  <path fill="#05a6f0" d="M1 12h10v10H1z" />
                  <path fill="#ffba08" d="M12 12h10v10H12z" />
                </svg>
              )}
              Continue with Microsoft
            </button>
            {fromMobileApp ? null : phoneButton}
          </div>

          {error && (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-row leading-snug text-destructive">
              {error}
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs tracking-[0.06em] text-muted-foreground" style={{ fontFamily: FONT_MONO }}>
              Secured by OAuth 2.0 · No data tracking, training
            </p>
            <a
              href="https://theubik.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-block text-xs tracking-[0.06em] text-muted-foreground underline underline-offset-2 hover:text-foreground"
              style={{ fontFamily: FONT_MONO }}
            >
              Privacy policy ↗
            </a>
          </div>
        </div>

        {/* ── Right: proof strip + component preview ──────────────────── */}
        {/* Below sm: no flex-1 growth — the panel sizes to its content
            instead of being stretched to fill the leftover min-h-dvh
            height in the mobile stacked layout, which is what was pushing
            the stat band way down from "Privacy policy" above. flex-1 (for
            the two-column width split) only kicks back in at lg. */}
        <div className="flex flex-col border-t border-border lg:flex-1 lg:border-l lg:border-t-0">
          {/* The stat band, preview, and trust row move as ONE cluster with a
              fixed gap between them — justify-center on this flex-1 wrapper
              puts any leftover viewport height as margin ABOVE the stat band
              and BELOW the trust row (equally), instead of the stat/trust
              bands staying pinned to the very top/bottom of the screen while
              only the preview centers. That's what pulls them in toward the
              middle. */}
          <div className="relative z-10 flex flex-col justify-start gap-4 py-4 sm:gap-6 sm:py-6 lg:flex-1 lg:justify-center lg:gap-8 lg:py-8">
            <div className="grid grid-cols-3 divide-x divide-primary-foreground/15 bg-primary text-primary-foreground">
              {STATS.map((s) => (
                <div key={s.value} className="px-4 py-4 sm:px-6">
                  <div className="text-xl font-semibold tabular-nums tracking-[-0.01em] sm:text-2xl" style={{ fontFamily: FONT_MONO }}>
                    {s.value}
                  </div>
                  <div className="mt-0.5 ubik-label opacity-80" style={{ fontFamily: FONT_MONO }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center px-8 sm:px-10">
              <ProductPreview />
            </div>

            <TrustCarousel downloadUrl={downloadUrl} />
          </div>
        </div>
      </div>
    </div>
  );
}
